import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { EventRecord } from "@/shared/domain/events";

import { MetadataForm, MetadataView } from "../../Metadata";
import { RobotoTypography } from "../../RobotoTypography";
import { AutocompleteType } from "../../TagAndMetadataAutocomplete";
import {
  useUpdateWorkspaceEvent,
  useWorkspaceEvent,
} from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventMetadata.module.css";

interface EventMetadataProps {
  eventId: EventRecord["event_id"];
  onError: (error: Error) => void;
}

export function EventMetadata({ eventId, onError }: EventMetadataProps) {
  const eventQuery = useWorkspaceEvent(eventId);
  const updateMetadataMutation = useUpdateWorkspaceEvent();

  const addMetadataToEvent = (key: string, value: string) => {
    updateMetadataMutation
      .mutateAsync({
        eventId,
        request: {
          metadata_changeset: {
            put_fields: { [key]: value },
          },
        },
      })
      .catch(onError);
  };

  const removeMetadataFromEvent = (key: string) => {
    updateMetadataMutation
      .mutateAsync({
        eventId,
        request: {
          metadata_changeset: {
            remove_fields: [key],
          },
        },
      })
      .catch(onError);
  };

  if (eventQuery.isPending) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Metadata
        </RobotoTypography>
        <MetadataForm
          metadata={{}}
          onAddMetadata={addMetadataToEvent}
          autocompleteType={AutocompleteType.EventMetadataKeys}
        />
      </section>
    );
  }

  if (eventQuery.isError) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Metadata
        </RobotoTypography>

        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <ErrorOutlineIcon color="error" />
          <RobotoTypography>Failed to load event tags</RobotoTypography>
        </div>
      </section>
    );
  }

  const { metadata } = eventQuery.data;

  return (
    <section>
      <RobotoTypography component={"h3"} className={styles.title}>
        Metadata
      </RobotoTypography>

      <MetadataView
        metadata={metadata}
        onDeleteMetadata={removeMetadataFromEvent}
      />
      <MetadataForm
        metadata={metadata}
        onAddMetadata={addMetadataToEvent}
        autocompleteType={AutocompleteType.EventMetadataKeys}
      />
    </section>
  );
}
